// Design tokens do painel de Funil.
// Referência: Design System — Painel de Canais (Configuração do Canal).
//
// Linguagem visual:
// - Neutros zinc (#18181b → #a1a1aa) sobre fundo #f4f4f7; roxo (#6d28d9)
//   só como acento — aba ativa, ação primária, seleção, links.
// - Cor semântica comunica estado; nunca decora.
// - Conteúdo em largura total da área principal (sem card/max-width central).
// - Hierarquia por cabeçalho de seção (rótulo uppercase fora do cartão).

export const FUNIL_DESIGN = {
  // ---------- shell ----------
  root: 'flex flex-col min-h-full w-full bg-[#f4f4f7] text-[#18181b] animate-in fade-in slide-in-from-bottom-4 duration-500',
  fontStack: "'Plus Jakarta Sans', Manrope, ui-sans-serif, system-ui, sans-serif",
  container: 'w-full px-4 md:px-6 lg:px-8',
  pageBody: 'pb-[120px]',

  // ---------- barra de topo + abas ----------
  stickyHeader:
    'sticky top-0 z-30 bg-white border-b border-[#e7e7ec]',
  topbar: 'flex flex-col md:flex-row md:items-start gap-4 pt-[18px] pb-0',
  topbarIdentity: 'flex items-start gap-3.5 min-w-0 flex-1',
  title: 'text-[20px] font-bold tracking-[-0.02em] leading-tight text-[#18181b]',
  subtitle: 'mt-[5px] text-[13px] text-[#71717a] leading-[1.5]',
  topbarActions: 'flex items-center gap-2.5 flex-shrink-0 flex-wrap md:pt-[5px]',

  // ---------- abas ----------
  tabsList:
    'flex h-auto w-full items-center justify-start gap-[26px] rounded-none bg-transparent p-0 mt-3.5 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden backdrop-blur-none',
  tab:
    'flex-shrink-0 rounded-none bg-transparent px-0.5 py-0 pt-2.5 pb-3 text-[14px] font-medium text-[#71717a] whitespace-nowrap transition-colors hover:text-[#18181b] data-[state=active]:bg-transparent data-[state=active]:text-[#6d28d9] data-[state=active]:font-semibold data-[state=active]:shadow-[inset_0_-2px_0_#6d28d9]',
  tabsBody: 'pt-6',

  // ---------- cabeçalho de seção (fora do cartão) ----------
  sectionHeader: 'mt-[18px] first:mt-0 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3',
  sectionHeaderMain: 'min-w-0',
  sectionHeaderTitle: 'text-[13px] font-bold uppercase tracking-[0.06em] text-[#a1a1aa]',
  sectionHeaderSub: 'mt-1.5 text-[13px] text-[#71717a] leading-[1.5]',

  // ---------- cartões ----------
  stack: 'flex flex-col gap-3.5',
  card: 'bg-white border border-[#e7e7ec] rounded-[12px] p-[26px] overflow-hidden min-w-0',
  cardFlush: 'bg-white border border-[#e7e7ec] rounded-[12px] overflow-hidden min-w-0',
  cardRows: 'bg-white border border-[#e7e7ec] rounded-[12px] px-[26px] py-2 overflow-hidden min-w-0',
  cardTitle: 'text-[15px] font-semibold text-[#18181b]',
  cardSubtitle: 'mt-0.5 text-[13px] text-[#71717a] leading-[1.5]',
  cardHead: 'flex items-start justify-between gap-4',
  divider: 'h-px bg-[#f0f0f3]',

  // ---------- formulário ----------
  grid2: 'grid grid-cols-1 md:grid-cols-2 gap-6',
  field: 'min-w-0',
  label: 'block text-[13px] font-semibold text-[#18181b] mb-[7px]',
  hint: 'mt-[7px] text-[12px] text-[#a1a1aa] leading-[1.5]',
  input:
    'w-full box-border h-auto px-3.5 py-[11px] rounded-lg border border-[#d4d4d8] bg-white text-[14px] md:text-[14px] text-[#18181b] shadow-none transition-colors placeholder:text-[#a1a1aa] hover:border-[#b8b8c0] focus:border-[#8b5cf6] focus-visible:border-[#8b5cf6] focus-visible:ring-2 focus-visible:ring-[#c4b5fd] focus-visible:ring-offset-0',
  select:
    'w-full box-border h-auto min-h-[44px] px-3.5 py-[11px] rounded-lg border border-[#d4d4d8] bg-white text-[14px] md:text-[14px] font-normal text-[#18181b] shadow-none transition-colors hover:border-[#b8b8c0] focus:border-[#8b5cf6] focus:ring-2 focus:ring-[#c4b5fd]',
  textarea:
    'w-full box-border px-4 py-3.5 rounded-lg border border-[#d4d4d8] bg-white text-[14px] leading-[1.6] text-[#18181b] shadow-none resize-y transition-colors placeholder:text-[#a1a1aa] hover:border-[#b8b8c0] focus:border-[#8b5cf6] focus-visible:border-[#8b5cf6] focus-visible:ring-2 focus-visible:ring-[#c4b5fd] focus-visible:ring-offset-0',

  // ---------- toggle ----------
  toggleRow: 'flex items-center gap-4 py-[18px]',
  toggleRowDivided: 'border-b border-[#f0f0f3]',
  toggleRowMain: 'flex-1 min-w-0',
  toggleRowLabel: 'text-[14px] font-semibold text-[#18181b]',
  toggleRowHelp: 'mt-[3px] text-[13px] text-[#71717a] leading-[1.5] max-w-[620px]',
  switch: 'flex-shrink-0 data-[state=checked]:bg-[#6d28d9] data-[state=unchecked]:bg-[#d4d4d8]',

  // ---------- cartões de escolha ----------
  choiceGrid: 'grid grid-cols-1 md:grid-cols-2 gap-2.5',
  choiceCard: 'text-left px-3.5 py-[13px] rounded-lg border transition-colors cursor-pointer',
  choiceCardOn: 'bg-[#ede9fe] border-[#a78bfa]',
  choiceCardOff: 'bg-white border-[#e7e7ec] hover:border-[#d4d4d8]',
  choiceTitle: 'text-[13.5px] font-semibold',
  choiceTitleOn: 'text-[#5b21b6]',
  choiceTitleOff: 'text-[#18181b]',
  choiceHelp: 'mt-[3px] text-[12px] text-[#71717a] leading-[1.45]',

  // ---------- botões ----------
  primaryButton:
    'inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-[10px] bg-gradient-to-b from-[#8b5cf6] to-[#7c3aed] text-white text-[14px] font-semibold whitespace-nowrap shadow-[0_2px_8px_rgba(124,58,237,.32)] transition-colors hover:from-[#7c3aed] hover:to-[#6d28d9] disabled:from-[#e4e4e7] disabled:to-[#e4e4e7] disabled:text-[#a1a1aa] disabled:shadow-none disabled:cursor-not-allowed',
  ghostButton:
    'inline-flex items-center justify-center gap-2 px-4 py-[9px] rounded-lg border border-[#e7e7ec] bg-white text-[13px] font-semibold text-[#52525b] whitespace-nowrap transition-colors hover:bg-[#f4f4f7] hover:text-[#18181b] disabled:opacity-50 disabled:cursor-not-allowed',
  dangerButton:
    'inline-flex items-center justify-center gap-2 px-4 py-[9px] rounded-lg border border-[#e7e7ec] bg-white text-[13px] font-semibold text-[#b42318] whitespace-nowrap transition-colors hover:bg-[#fef3f2] hover:border-[#fecdca] disabled:opacity-50 disabled:cursor-not-allowed',
  iconButton:
    'inline-flex items-center justify-center w-8 h-8 rounded-lg border border-[#e7e7ec] bg-white text-[#71717a] transition-colors hover:bg-[#f4f4f7] hover:text-[#18181b]',
  iconButtonDanger:
    'inline-flex items-center justify-center w-8 h-8 rounded-lg border border-[#e7e7ec] bg-white text-[#b42318] transition-colors hover:bg-[#fef3f2] hover:border-[#fecdca]',
  link: 'text-[13px] font-semibold text-[#6d28d9] transition-colors hover:text-[#5b21b6] hover:underline',

  // ---------- badges ----------
  badge: 'inline-flex items-center gap-1.5 px-2.5 py-[3px] rounded-md text-[12px] font-semibold whitespace-nowrap flex-shrink-0 border',
  badgeOk: 'bg-[#ecfdf3] text-[#067647] border-[#abefc6]',
  badgeNeutral: 'bg-[#f4f4f5] text-[#71717a] border-[#e7e7ec]',
  badgeSoft: 'bg-[#ede9fe] text-[#5b21b6] border-[#ddd6fe]',
  badgeDot: 'w-1.5 h-1.5 rounded-full',
  badgeDotOk: 'bg-[#17b26a]',
  badgeDotNeutral: 'bg-[#d4d4d8]',

  // ---------- tabela ----------
  tableHead: 'text-[12px] font-semibold uppercase tracking-[0.04em] text-[#a1a1aa]',
  tableCell: 'text-[14px] text-[#18181b]',
  tableCellMuted: 'text-[13px] text-[#71717a]',
  tableRow: 'border-b border-[#f0f0f3] last:border-b-0 hover:bg-[#fafafb]',

  // ---------- empty / notice ----------
  emptyState: 'px-2 py-10 text-center',
  emptyIcon:
    'mx-auto mb-4 flex items-center justify-center w-14 h-14 rounded-[12px] bg-[#f4f4f5] text-[#a1a1aa]',
  emptyTitle: 'text-[15px] font-semibold text-[#18181b]',
  emptyDesc: 'mt-1.5 text-[13px] text-[#71717a] leading-[1.5] max-w-sm mx-auto',
  noticeInfo: 'px-4 py-3.5 rounded-[10px] border border-[#e7e7ec] bg-[#fafafb] text-[13px] text-[#71717a] leading-[1.5]',

  // ---------- pipeline kanban ----------
  pipelineGrid: 'grid gap-3.5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5',
  pipelineCard:
    'bg-white border border-[#e7e7ec] rounded-[12px] p-[18px] flex flex-col gap-3 min-w-0 overflow-hidden',
  pipelineCardTitle: 'text-[15px] font-semibold text-[#18181b] truncate min-w-0',
  pipelineStageList: 'flex flex-col gap-1.5 list-none m-0 p-0 min-w-0 w-full overflow-hidden',
  pipelineStage:
    'flex items-center justify-between gap-2 min-w-0 w-full px-[13px] py-[11px] rounded-lg border border-[#e7e7ec] bg-white text-[13.5px] font-medium text-[#18181b] cursor-grab active:cursor-grabbing hover:border-[#d4d4d8] overflow-hidden',
  pipelineStageDragging: 'border-[#a78bfa] bg-[#ede9fe]',
  pipelineAddStage:
    'flex items-center justify-center gap-1.5 w-full min-w-0 px-3 py-[9px] rounded-lg border border-dashed border-[#d4d4d8] bg-transparent text-[13px] font-semibold text-[#71717a] transition-colors hover:border-[#a78bfa] hover:text-[#6d28d9] hover:bg-[#ede9fe]/40',

  // ---------- lista ordenável (layout) ----------
  sortItem:
    'flex items-center gap-3 px-[13px] py-[11px] rounded-lg border border-[#e7e7ec] bg-white cursor-grab active:cursor-grabbing transition-colors hover:border-[#d4d4d8]',
  sortItemDragging: 'border-[#a78bfa] bg-[#ede9fe]',
  sortIndex:
    'flex items-center justify-center w-5 h-5 rounded-full bg-[#ede9fe] text-[11px] font-bold text-[#6d28d9] flex-shrink-0',

  // ---------- disclosure / accordion ----------
  disclosure:
    'bg-white border border-[#e7e7ec] rounded-[12px] overflow-hidden min-w-0',
  disclosureButton:
    'w-full flex items-center gap-3 px-[18px] py-4 text-left cursor-pointer transition-colors hover:bg-[#fafafb] min-w-0 overflow-hidden',
  disclosureCaret: 'text-[11px] text-[#a1a1aa] w-2.5 flex-shrink-0 transition-transform',
  disclosureCaretOpen: 'rotate-90',
  disclosureBody: 'border-t border-[#f0f0f3] overflow-x-auto',

  // ---------- dialog / sheet ----------
  dialogTitle: 'text-[15px] font-semibold text-[#18181b]',
  dialogDesc: 'text-[13px] text-[#71717a] leading-[1.5]',
  dialogFooter: 'flex justify-end gap-2.5',
  sheetContent: 'w-full sm:max-w-md bg-white p-0 flex flex-col overflow-hidden',
  sheetHeader: 'px-[26px] py-[22px] border-b border-[#e7e7ec]',
  sheetBody: 'flex-1 px-[26px] py-[22px] flex flex-col gap-5 overflow-y-auto min-h-0',
  sheetFooter: 'px-[26px] py-4 border-t border-[#e7e7ec] bg-[#fafafb] flex justify-end gap-2.5',

  // ---------- chips ----------
  chip:
    'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#ede9fe] text-[13px] font-semibold text-[#5b21b6] max-w-full',
  chipRemove: 'text-[#8b5cf6] hover:text-[#5b21b6] flex-shrink-0',

  // ---------- table wrap ----------
  tableWrap: 'w-full overflow-x-auto min-w-0',
};

// Alias legado — componentes que ainda importam o nome antigo.
export const FUNIL_COMPONENT_DESIGN = FUNIL_DESIGN;

export default FUNIL_DESIGN;
